import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Grid,
} from '@mui/material';
import { ArrowBack, AddCircle, Delete, Save } from '@mui/icons-material';
import { PageHeader, headerCellSx } from '../../components/common/pageKit';
import { api } from '../../services/apiClient';
import { projectService, User } from '../../services/projectService';

// ---------------------------------------------------------------------------
// Create / edit a Customer Enquiry. Mirrors RfqFormPage.tsx's structure:
// header fields plus a dynamic item-rows table. Editing is only legal while
// the enquiry is OPEN, same rule the backend enforces server-side.
// ---------------------------------------------------------------------------

type EnquirySource = 'DIRECT' | 'REFERRAL' | 'WEBSITE' | 'EXHIBITION' | 'CAMPAIGN' | 'EXISTING_CUSTOMER' | 'OTHER';
type EnquiryPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

const SOURCES: EnquirySource[] = ['DIRECT', 'REFERRAL', 'WEBSITE', 'EXHIBITION', 'CAMPAIGN', 'EXISTING_CUSTOMER', 'OTHER'];
const PRIORITIES: EnquiryPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

interface Customer {
  id: number | string;
  customer_code?: string;
  company_name: string;
  contact_person?: string;
}

interface Product {
  id: string | number;
  product_name: string;
  hsn_code?: string;
  unit?: string;
  is_active?: boolean;
}

let draftKeySeq = 0;
const nextDraftKey = () => `draft-${++draftKeySeq}`;

interface ItemDraft {
  key: string;
  product: Product | null;
  description: string;
  quantity: string;
  uom: string;
  targetPrice: string;
  requiredDeliveryDate: string;
}

const emptyItem = (): ItemDraft => ({
  key: nextDraftKey(),
  product: null,
  description: '',
  quantity: '1',
  uom: '',
  targetPrice: '',
  requiredDeliveryDate: '',
});

const toNum = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

interface EnquiryItemFromApi {
  product_id: string | number | null;
  product_name?: string | null;
  description: string | null;
  quantity: number | string;
  uom: string | null;
  target_price: number | string | null;
  required_delivery_date: string | null;
}

interface EnquiryFromApi {
  id: number;
  customer_id: string | number;
  contact_person?: string | null;
  enquiry_date: string;
  valid_until: string | null;
  salesperson_id?: string | number | null;
  source: EnquirySource;
  probability?: number | string | null;
  priority: EnquiryPriority;
  notes: string | null;
  status: string;
  items: EnquiryItemFromApi[];
}

const CustomerEnquiryFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [salespeople, setSalespeople] = useState<User[]>([]);
  const [loadingRefData, setLoadingRefData] = useState(true);
  const [loadingEnquiry, setLoadingEnquiry] = useState(isEdit);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [contactPerson, setContactPerson] = useState('');
  const [enquiryDate, setEnquiryDate] = useState(todayISO());
  const [validUntil, setValidUntil] = useState('');
  const [salespersonId, setSalespersonId] = useState('');
  const [source, setSource] = useState<EnquirySource>('DIRECT');
  const [probability, setProbability] = useState('');
  const [priority, setPriority] = useState<EnquiryPriority>('MEDIUM');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([emptyItem()]);
  const [status, setStatus] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    setLoadingRefData(true);
    Promise.all([
      api.get<{ customers: Customer[] }>('/sales/customers').catch(() => ({ customers: [] as Customer[] })),
      api.get<{ products: Product[] }>('/products').catch(() => ({ products: [] as Product[] })),
      projectService.getUsers().catch(() => [] as User[]),
    ])
      .then(([customersRes, productsRes, usersRes]) => {
        setCustomers(customersRes.customers || []);
        setProducts((productsRes.products || []).filter((p) => p.is_active !== false));
        setSalespeople(usersRes || []);
      })
      .finally(() => setLoadingRefData(false));
  }, []);

  useEffect(() => {
    if (!isEdit || !id || loadingRefData) return;
    setLoadingEnquiry(true);
    api
      .get<{ enquiry: EnquiryFromApi }>(`/customer-enquiries/${id}`)
      .then(({ enquiry }) => {
        setCustomer(customers.find((c) => String(c.id) === String(enquiry.customer_id)) || null);
        setContactPerson(enquiry.contact_person || '');
        setEnquiryDate(enquiry.enquiry_date ? enquiry.enquiry_date.slice(0, 10) : todayISO());
        setValidUntil(enquiry.valid_until ? enquiry.valid_until.slice(0, 10) : '');
        setSalespersonId(enquiry.salesperson_id ? String(enquiry.salesperson_id) : '');
        setSource(enquiry.source || 'DIRECT');
        setProbability(enquiry.probability !== null && enquiry.probability !== undefined ? String(enquiry.probability) : '');
        setPriority(enquiry.priority || 'MEDIUM');
        setNotes(enquiry.notes || '');
        setStatus(enquiry.status);
        setItems(
          (enquiry.items || []).map((it) => ({
            key: nextDraftKey(),
            product: products.find((p) => String(p.id) === String(it.product_id)) || null,
            description: it.description || it.product_name || '',
            quantity: String(toNum(it.quantity) || 1),
            uom: it.uom || '',
            targetPrice: it.target_price !== null && it.target_price !== undefined ? String(toNum(it.target_price)) : '',
            requiredDeliveryDate: it.required_delivery_date ? it.required_delivery_date.slice(0, 10) : '',
          }))
        );
      })
      .catch((err: any) => setError(err.message || 'Failed to load enquiry'))
      .finally(() => setLoadingEnquiry(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, loadingRefData]);

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (key: string) => setItems((prev) => prev.filter((it) => it.key !== key));
  const updateItem = (key: string, patch: Partial<ItemDraft>) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  const handleProductChange = (key: string, product: Product | null) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.key !== key) return it;
        return {
          ...it,
          product,
          uom: it.uom || product?.unit || '',
          description: it.description || product?.product_name || '',
        };
      })
    );
  };

  const canEdit = !isEdit || status === 'OPEN';

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!customer) errs.push('Select the customer this enquiry is from.');
    if (!enquiryDate) errs.push('Enquiry date is required.');
    if (items.length === 0) errs.push('At least one line item is required.');
    items.forEach((it, idx) => {
      if (toNum(it.quantity) <= 0) errs.push(`Line ${idx + 1}: quantity must be greater than 0.`);
    });
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload: Record<string, any> = {
        customerId: customer!.id,
        contactPerson: contactPerson.trim() || undefined,
        enquiryDate,
        validUntil: validUntil || undefined,
        salespersonId: salespersonId ? Number(salespersonId) : undefined,
        source,
        probability: probability ? toNum(probability) : undefined,
        priority,
        notes: notes.trim() || undefined,
        items: items.map((it) => ({
          productId: it.product?.id || undefined,
          description: it.description.trim() || it.product?.product_name || undefined,
          quantity: toNum(it.quantity),
          uom: it.uom.trim() || undefined,
          targetPrice: it.targetPrice ? toNum(it.targetPrice) : undefined,
          requiredDeliveryDate: it.requiredDeliveryDate || undefined,
        })),
      };

      const response =
        isEdit && id
          ? await api.put<{ enquiry: { id: number } }>(`/customer-enquiries/${id}`, payload)
          : await api.post<{ enquiry: { id: number } }>('/customer-enquiries', payload);

      navigate(`/sales/enquiries/${response.enquiry.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to save enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = loadingRefData || loadingEnquiry;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(isEdit && id ? `/sales/enquiries/${id}` : '/sales/enquiries')}
        sx={{ mb: 1 }}
      >
        Back
      </Button>

      <PageHeader
        title={isEdit ? 'Edit Enquiry' : 'New Customer Enquiry'}
        subtitle="Capture what the customer is asking for so it can be turned into a quotation."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!canEdit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This enquiry is {status} and can no longer be edited.
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((e) => (
            <Box key={e}>{e}</Box>
          ))}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Enquiry Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Autocomplete
              size="small"
              options={customers}
              value={customer}
              onChange={(_, v) => setCustomer(v)}
              getOptionLabel={(o) => o.company_name || ''}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              disabled={!canEdit}
              renderInput={(params) => <TextField {...params} label="Customer *" placeholder="Select customer" />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Contact Person"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Salesperson"
              value={salespersonId}
              onChange={(e) => setSalespersonId(e.target.value)}
              disabled={!canEdit}
            >
              <MenuItem value="">Not assigned</MenuItem>
              {salespeople.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.full_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Enquiry Date"
              value={enquiryDate}
              onChange={(e) => setEnquiryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Valid Until"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Source"
              value={source}
              onChange={(e) => setSource(e.target.value as EnquirySource)}
              disabled={!canEdit}
            >
              {SOURCES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as EnquiryPriority)}
              disabled={!canEdit}
            >
              {PRIORITIES.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Win Probability %"
              value={probability}
              onChange={(e) => setProbability(e.target.value)}
              inputProps={{ min: 0, max: 100 }}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 2.5 }}>
        <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Line Items
          </Typography>
          {canEdit && (
            <Button size="small" variant="outlined" startIcon={<AddCircle />} onClick={addItem}>
              Add Item
            </Button>
          )}
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Product</TableCell>
                <TableCell sx={headerCellSx} align="right">Qty</TableCell>
                <TableCell sx={headerCellSx}>UOM</TableCell>
                <TableCell sx={headerCellSx} align="right">Target Price</TableCell>
                <TableCell sx={headerCellSx}>Required By</TableCell>
                {canEdit && <TableCell sx={headerCellSx} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.key} hover>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Autocomplete
                      size="small"
                      options={products}
                      value={it.product}
                      onChange={(_, v) => handleProductChange(it.key, v)}
                      getOptionLabel={(o) => `${o.product_name}${o.hsn_code ? ` (${o.hsn_code})` : ''}`}
                      isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                      disabled={!canEdit}
                      renderInput={(params) => <TextField {...params} placeholder="Select product" />}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 90 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={it.quantity}
                      onChange={(e) => updateItem(it.key, { quantity: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      disabled={!canEdit}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 90 }}>
                    <TextField
                      size="small"
                      value={it.uom}
                      onChange={(e) => updateItem(it.key, { uom: e.target.value })}
                      disabled={!canEdit}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 110 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={it.targetPrice}
                      onChange={(e) => updateItem(it.key, { targetPrice: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      disabled={!canEdit}
                      sx={{ minWidth: 100 }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <TextField
                      size="small"
                      type="date"
                      value={it.requiredDeliveryDate}
                      onChange={(e) => updateItem(it.key, { requiredDeliveryDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={!canEdit}
                      sx={{ minWidth: 140 }}
                    />
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => removeItem(it.key)} disabled={items.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={() => navigate(isEdit && id ? `/sales/enquiries/${id}` : '/sales/enquiries')}>Cancel</Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Enquiry'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CustomerEnquiryFormPage;
